import Keycloak, {
    KeycloakTokenParsed as BaseKeycloakTokenParsed,
    KeycloakLoginOptions,
} from 'keycloak-js';
import React, { createContext } from 'react';

import { KeycloakRoleMalFormedError } from './KeycloakRoleMalFormedError';

export interface KeycloakTokenParsed extends BaseKeycloakTokenParsed {
    name?: string;
    email?: string;
}

export type KeycloakRoleDefinition = undefined | string | string[];

// ROLE / REALM CONSTANTS
export const REALM_ADMIN = 'realm:admin';
export const REALM_COMPTA = 'realm:compta';
export const REALM_DEBUG = 'realm:debug';
export const REALM_FORMALIST = 'realm:formaliste';
export const REALM_INTERN = 'realm:interne';
export const REALM_RESP_COMPTA = 'realm:responsable compta';
export const REALM_RESP_FORMALIST = 'realm:responsable formaliste';

// FUNCTIONAL ROLE
export const ROLE_API_SIGN_READ = 'api-sign:dossier-read';
export const ROLE_API_SIGN_WRITE = 'api-sign:dossier-write';
export const ROLE_API_SIGN_ADMIN = 'api-sign:admin';
export const ROLE_API_ACHAT_ADMIN = 'api-achats:administrator';

export class KeycloakService {
    private initialized: boolean;

    private keycloakInstance: Keycloak;

    public constructor(keycloak: Keycloak, initialized: boolean) {
        this.keycloakInstance = keycloak;
        this.initialized = initialized;
    }

    public isInitialized() {
        return this.initialized;
    }

    public checkRole(role?: KeycloakRoleDefinition, oneOfRole: boolean = false): boolean {
        if (!this.keycloakInstance.authenticated) {
            return false;
        }

        if (role === undefined) {
            return this.keycloakInstance.authenticated;
        }

        if (typeof role === 'string') {
            const splitedRole = role.split(':');

            if (splitedRole.length === 1) {
                return this.keycloakInstance.hasResourceRole(splitedRole[0]);
            }
            if (splitedRole.length === 2) {
                if (splitedRole[0] === 'realm') {
                    return this.keycloakInstance.hasRealmRole(splitedRole[1]);
                }
                return this.keycloakInstance.hasResourceRole(splitedRole[1], splitedRole[0]);
            }
            throw new KeycloakRoleMalFormedError();
        } else {
            if (oneOfRole) {
                return role.some((item) => this.checkRole(item));
            }
            // The current user has one of the roles defined as param
            return role.map((item) => this.checkRole(item)).indexOf(false) === -1;
        }
    }

    public getKc() {
        return this.keycloakInstance;
    }

    public accountManagement() {
        return this.getKc().accountManagement();
    }

    public isAutenticated() {
        return this.keycloakInstance.authenticated || false;
    }

    public getName() {
        if (typeof this.getTokenParsed() === 'object') {
            return this.getTokenParsed().name || '';
        }
        return '';
    }

    public getEmail() {
        if (typeof this.getTokenParsed() === 'object') {
            return this.getTokenParsed().email || '';
        }
        return '';
    }

    public getTokenParsed() {
        return this.getKc().tokenParsed as KeycloakTokenParsed;
    }

    public createLoginUrl() {
        return this.goToAuthServer();
    }

    public createUpdatePasswordUrl() {
        return this.goToAuthServer({ action: 'UPDATE_PASSWORD' });
    }

    public createTotpUrl() {
        return this.goToAuthServer({ action: 'CONFIGURE_TOTP' });
    }

    public createUpdateProfileUrl() {
        return this.goToAuthServer({ action: 'UPDATE_PROFILE' });
    }

    public createVerifyEmailUrl() {
        return this.goToAuthServer({ action: 'VERIFY_EMAIL' });
    }

    public createTermsAndConditionsUrl() {
        return this.goToAuthServer({ action: 'TERMS_AND_CONDITIONS' });
    }

    public goToAuthServer(options?: KeycloakLoginOptions) {
        const innerOptions = { locale: 'fr', ...options };
        return this.getKc().createLoginUrl(innerOptions);
    }
}

export type KeycloakContextType = {
    keycloakInstance: Keycloak | null;
    setKeycloakInstance: React.Dispatch<React.SetStateAction<Keycloak>>;
    initialized: boolean;
    setInitialized: React.Dispatch<React.SetStateAction<boolean>>;
    keycloakService?: KeycloakService;
    setKeycloakService: React.Dispatch<React.SetStateAction<KeycloakService | undefined>>;
};

export const KeycloakContext = createContext<KeycloakContextType>({
    keycloakInstance: null,
    setKeycloakInstance: () => {},
    initialized: false,
    setInitialized: () => {},
    keycloakService: undefined,
    setKeycloakService: () => {},
});
